import { ArrowRight, Clock, MapPin, Navigation, Phone } from "lucide-react";
import { ASSET, ADDRESS, DIRECTIONS_URL, PHONE } from "@/lib/site";
import { useOpenStatus } from "@/hooks/useOpenStatus";

const HeroCopy = ({ t }) => (
  <div className="hero-copy">
    <h1 data-testid="hero-headline" className="hero-h1">
      <span className="hero-welcome hero-in" style={{ "--d": "100ms" }}>{t.hero.welcome}</span>
      <span className="hero-word font-display hero-in" style={{ "--d": "220ms" }}>Outlet</span>
      <span className="hero-word hero-word--red font-display hero-in" style={{ "--d": "340ms" }}>Båstad</span>
    </h1>
    <p className="hero-cats font-display hero-in" style={{ "--d": "460ms" }}>
      {t.hero.categories}
    </p>
    <p className="hero-tagline hero-in" style={{ "--d": "560ms" }}>
      {t.hero.tagline}
    </p>
    <a href="#hitta" data-testid="hero-cta-hitta-hit" className="hero-btn font-display hero-in group" style={{ "--d": "680ms" }}>
      {t.nav.find} <ArrowRight className="hero-btn-icon transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
    </a>
  </div>
);

const HeroPhotos = ({ t }) => (
  <div className="hero-photos">
    <div className="hero-main">
      <img
        src={ASSET("butik-dag.webp")}
        alt={t.hero.photoAlt}
        width="1672"
        height="941"
        fetchPriority="high"
        className="h-full w-full object-cover"
      />
    </div>
  </div>
);

const InfoItem = ({ icon: Icon, filled, title, lines, testid, href, external, extra }) => (
  <a
    href={href}
    data-testid={testid}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    className="hero-info-item group"
  >
    <Icon className="hero-info-icon" fill={filled ? "currentColor" : "none"} stroke={filled ? "#000" : "currentColor"} strokeWidth={filled ? 1.6 : 2} aria-hidden="true" />
    <span className="min-w-0">
      <span className="hero-info-title">{title}{extra}</span>
      {lines.map((l) => <span key={l} className="hero-info-line">{l.replace(/ (\d)/g, "\u00a0$1")}</span>)}
    </span>
  </a>
);

const OpenDot = ({ t }) => {
  const s = useOpenStatus();
  const translatedDetail = `${s.detailType === "closes" ? t.hero.closesAt : s.detailType === "opensToday" ? t.hero.opensToday : t.hero.opensTomorrow} ${s.detailHour}`;
  return (
    <span className="hero-open" data-testid="hero-open-status" title={`${s.isOpen ? t.hero.open : t.hero.closed} · ${translatedDetail}`}>
      <span className={`hero-open-dot ${s.isOpen ? "bg-green-500 pulse-dot" : "bg-[var(--red)]"}`} aria-hidden="true" />
      <span data-testid="hero-open-status-label" className="hero-open-label">{s.isOpen ? t.hero.open : t.hero.closed}</span>
    </span>
  );
};

const InfoBar = ({ t }) => (
  <div className="hero-bar" data-testid="hero-info-bar">
    <InfoItem icon={MapPin} filled title={t.hero.address} lines={[ADDRESS.street, ADDRESS.postal]} href={DIRECTIONS_URL} external testid="hero-info-address-link" />
    <InfoItem icon={Clock} title={t.hero.hours} lines={[t.hero.weekdayHours, t.hero.weekendHours]} href="#hitta" testid="hero-info-hours-link" extra={<OpenDot t={t} />} />
    <InfoItem icon={Phone} filled title={t.hero.phone} lines={[PHONE.display]} href={PHONE.href} testid="hero-info-phone-link" />
    <InfoItem icon={Navigation} title={t.hero.mapTitle} lines={[t.hero.mapLine1, t.hero.mapLine2]} href={DIRECTIONS_URL} external testid="hero-info-map-link" />
  </div>
);

export const Hero = ({ t }) => (
  <section id="hem" data-testid="hero-section" className="hero bg-black text-white" style={{ "--red": "#da2029", "--red-hover": "#b9161e" }}>
    <div className="hero-stage">
      <HeroPhotos t={t} />
      <div className="hero-offer-layout">
        <HeroCopy t={t} />
        <p className="hero-discount" data-testid="hero-discount">
          <strong className="hero-discount-number">{t.hero.discountRange}</strong>
          <span className="hero-discount-label">{t.hero.discountLabel}</span>
        </p>
      </div>
      <InfoBar t={t} />
    </div>
  </section>
);
